import { useCallback, useSyncExternalStore } from "react";

const STORAGE_KEY = "bastion.secondarySidebar.widthPx";

export const SECONDARY_SIDEBAR_DEFAULT_WIDTH_PX = 240;
const MIN_WIDTH_PX = 180;
const MAX_WIDTH_PX = 480;

const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) {
    listener();
  }
}

function clampWidthPx(value: number): number {
  return Math.max(MIN_WIDTH_PX, Math.min(MAX_WIDTH_PX, Math.round(value)));
}

function readWidthPx(): number {
  if (typeof window === "undefined") {
    return SECONDARY_SIDEBAR_DEFAULT_WIDTH_PX;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return SECONDARY_SIDEBAR_DEFAULT_WIDTH_PX;
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed)) return SECONDARY_SIDEBAR_DEFAULT_WIDTH_PX;
    return clampWidthPx(parsed);
  } catch {
    return SECONDARY_SIDEBAR_DEFAULT_WIDTH_PX;
  }
}

function getServerSnapshot(): number {
  return SECONDARY_SIDEBAR_DEFAULT_WIDTH_PX;
}

function subscribe(onStoreChange: () => void) {
  listeners.add(onStoreChange);
  const onStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY || event.key === null) {
      onStoreChange();
    }
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(onStoreChange);
    window.removeEventListener("storage", onStorage);
  };
}

function persistWidthPx(widthPx: number) {
  if (typeof window === "undefined") return;
  const next = clampWidthPx(widthPx);
  try {
    window.localStorage.setItem(STORAGE_KEY, String(next));
  } catch {
    // ignore
  }
  emit();
}

export function useSecondarySidebarWidth() {
  const widthPx = useSyncExternalStore(subscribe, readWidthPx, getServerSnapshot);

  const setWidthPx = useCallback((next: number) => {
    persistWidthPx(next);
  }, []);

  const resetWidthPx = useCallback(() => {
    persistWidthPx(SECONDARY_SIDEBAR_DEFAULT_WIDTH_PX);
  }, []);

  return { widthPx, setWidthPx, resetWidthPx };
}
